import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertCircle, Info } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

interface RiskSettingsProps {
  form: any;
  setForm: React.Dispatch<React.SetStateAction<any>>;
  row: (label: string, inputEl: React.ReactNode, helper?: string) => React.ReactNode;
  field: (key: string) => any;
  isReadOnly: boolean;
}

export const RiskSettings: React.FC<RiskSettingsProps> = ({
  form, setForm, row, field, isReadOnly
}) => {
  return (
    <div className={`pt-6 border-t border-border space-y-6 ${isReadOnly ? 'opacity-60 pointer-events-none' : ''}`}>
      <h3 className="text-xs font-black text-danger uppercase tracking-widest flex items-center gap-2">
        <AlertCircle className="w-3.5 h-3.5" /> 위험 관리 (Risk Management)
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
        {row('손절 ROE %', (
          <div className="w-full relative flex items-center gap-2">
            <div className="relative flex-1">
              <input
                type="number"
                step="0.1"
                className={`w-full h-10 rounded-xl border border-border pl-3 pr-8 font-black text-sm transition-all ${form.use_variable_stop_loss
                    ? 'bg-muted text-muted-foreground opacity-70 cursor-not-allowed'
                    : 'bg-background'
                  }`}
                {...field('sl_pct')}
                value={form.use_variable_stop_loss ? (form.variable_sl_low_pct ?? '') : (form.sl_pct ?? '')}
                disabled={form.use_variable_stop_loss || isReadOnly}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-danger">%</span>
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-lg bg-muted hover:bg-muted/80 text-muted-foreground transition-colors"><Info className="w-3.5 h-3.5" /></button>
              </PopoverTrigger>
              <PopoverContent className="w-80 space-y-3">
                <h4 className="text-[11px] font-black text-muted-foreground uppercase tracking-wider">추천 손절 ROE%</h4>
                <div className="space-y-2 text-[10px]">
                  <div className="p-2 bg-muted/50 rounded-xl border border-border/50">
                    <p className="font-bold text-muted-foreground">0 = 손절 없음</p>
                    <p className="text-muted-foreground">수동 청산 또는 SL 비활성화. 위험 관리 없이 포지션 유지</p>
                  </div>
                  <div className="p-2.5 bg-danger/5 rounded-xl border border-danger/10 space-y-1">
                    <p className="font-bold text-danger">타이트 (step 4 이하 컷) — sl_pct 40~47</p>
                    <p className="text-muted-foreground">손실 폭 작음, 잦은 손절 가능. 변동성 클 때 step 5 진입 전 정리됨</p>
                  </div>
                  <div className="p-2.5 bg-amber-500/5 rounded-xl border border-amber-500/10 space-y-1">
                    <p className="font-bold text-amber-600">밸런스 — sl_pct 60~70</p>
                    <p className="text-muted-foreground">ATR 3.0~3.5배 수준. 변동성 따라 적정 손절. 대부분의 시나리오에서 무난</p>
                  </div>
                  <div className="p-2.5 bg-blue-500/5 rounded-xl border border-blue-500/10 space-y-1">
                    <p className="font-bold text-blue-500">와이드 — sl_pct 80~100</p>
                    <p className="text-muted-foreground">추세 끝까지 탑승. 손실 폭 크나 터질 확률 낮음. 변동성 심한 종목에 적합</p>
                  </div>
                </div>
                <div className="text-[9px] text-muted-foreground/70 border-t border-border/50 pt-2 italic">sl_pct는 ROE% 기준. 레버리지 20x 기준.</div>
              </PopoverContent>
            </Popover>
          </div>
        ))}

        {row('손절 타이밍', (
          <div className="flex bg-muted/50 p-1 rounded-lg gap-1 w-full">
            <Button variant={form.sl_strategy === 'realtime' ? "default" : "ghost"} size="sm" onClick={() => setForm({ ...form, sl_strategy: 'realtime' })} className={`flex-1 h-8 rounded-md text-[10px] font-bold ${form.sl_strategy === 'realtime' ? 'bg-primary text-white shadow-sm' : ''}`}>실시간</Button>
            <Button variant={form.sl_strategy === 'candle_close' ? "default" : "ghost"} size="sm" onClick={() => setForm({ ...form, sl_strategy: 'candle_close' })} className={`flex-1 h-8 rounded-md text-[10px] font-bold ${form.sl_strategy === 'candle_close' ? 'bg-success text-white shadow-sm' : ''}`}>봉 마감</Button>
          </div>
        ))}

        {row('손절 시 봇 정지', (
          <div className="flex items-center gap-2 h-10">
            <input
              type="checkbox"
              className="w-4 h-4 rounded text-primary cursor-pointer"
              checked={form.stop_bot_on_sl || false}
              onChange={(e) => setForm({ ...form, stop_bot_on_sl: e.target.checked })}
              disabled={isReadOnly}
            />
            <span className="text-[11px] font-bold text-muted-foreground">손절(SL) 발생 시 봇을 자동으로 비활성화합니다.</span>
          </div>
        ))}

        {row('강제 청산 시 봇 정지', (
          <div className="flex items-center gap-2 h-10">
            <input
              type="checkbox"
              className="w-4 h-4 rounded text-primary cursor-pointer"
              checked={form.stop_bot_on_liquidation || false}
              onChange={(e) => setForm({ ...form, stop_bot_on_liquidation: e.target.checked })}
              disabled={isReadOnly}
            />
            <span className="text-[11px] font-bold text-muted-foreground">거래소 강제 청산(Liquidation) 감지 시 봇을 자동으로 비활성화합니다.</span>
          </div>
        ))}

        {row('상한 정지 가격', (
          <div className="w-full">
            <div className="flex gap-2 w-full">
              <div className="w-3/5 relative">
                <input type="number" step="0.0001" className="w-full h-10 rounded-xl border border-danger/20 pl-3 pr-3 font-black bg-danger/[0.02] focus:ring-2 focus:ring-danger focus:outline-none text-sm " {...field('stop_price_upper')} />
              </div>
              <div className="w-2/5">
                <select className="w-full h-10 rounded-xl border border-danger/20 pl-3 md:pl-1 pr-2 font-bold bg-danger/[0.02] focus:ring-2 focus:ring-danger focus:outline-none text-[11px] " {...field('stop_price_upper_side')}>
                  <option value="both">전체 정리 후 봇정지</option>
                  <option value="long">Long 정리 후 해당 포지션 정지</option>
                  <option value="short">Short 정리 후 해당 포지션 정지</option>
                </select>
              </div>
            </div>
            <p className="text-[9px] text-danger/60 mt-1 font-semibold leading-normal">
              ⚠️ 설정 가격 이상으로 가격이 도달시(실시간) 시장가 매도후 정지 (전체 또는 해당 포지션만)
            </p>
          </div>
        ))}

        {row('하한 정지 가격', (
          <div className="w-full">
            <div className="flex gap-2 w-full">
              <div className="w-3/5 relative">
                <input type="number" step="0.0001" className="w-full h-10 rounded-xl border border-danger/20 pl-3 pr-3 font-black bg-danger/[0.02] focus:ring-2 focus:ring-danger focus:outline-none text-sm " {...field('stop_price_lower')} />
              </div>
              <div className="w-2/5">
                <select className="w-full h-10 rounded-xl border border-danger/20 pl-3 md:pl-1 pr-2 font-bold bg-danger/[0.02] focus:ring-2 focus:ring-danger focus:outline-none text-[11px] " {...field('stop_price_lower_side')}>
                  <option value="both">전체 정리 후 봇정지</option>
                  <option value="long">Long 정리 후 해당 포지션 정지</option>
                  <option value="short">Short 정리 후 해당 포지션 정지</option>
                </select>
              </div>
            </div>
            <p className="text-[9px] text-danger/60 mt-1 font-semibold leading-normal">
              ⚠️ 설정 가격 이하로 가격이 도달시(실시간) 포지션 시장가 매도후 정지 (전체 또는 해당 포지션만)
            </p>
          </div>
        ))}

        <div className="col-span-full bg-danger/5 border border-danger/10 rounded-2xl p-4 space-y-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Badge className="bg-danger text-white border-none text-[10px] font-black uppercase">Variable SL</Badge>
              <h4 className="text-[14px] font-black text-danger uppercase">가변 손절 설정 (DCA 차수별)</h4>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-muted-foreground uppercase">{form.use_variable_stop_loss ? '활성화됨' : '비활성'}</span>
              <input type="checkbox" className="w-5 h-5 rounded-lg border-border text-danger cursor-pointer" checked={form.use_variable_stop_loss || false} onChange={(e) => {
                const nextVal = e.target.checked;
                setForm((f: any) => {
                  const nextLowPct = nextVal ? (f.variable_sl_low_pct && Number(f.variable_sl_low_pct) > 0 ? f.variable_sl_low_pct : 55.0) : 55.0;
                  return {
                    ...f,
                    use_variable_stop_loss: nextVal,
                    variable_sl_threshold_step: nextVal ? (f.variable_sl_threshold_step && Number(f.variable_sl_threshold_step) > 0 ? f.variable_sl_threshold_step : 4) : 4,
                    variable_sl_low_pct: nextLowPct,
                    variable_sl_high_pct: nextVal ? (f.variable_sl_high_pct && Number(f.variable_sl_high_pct) > 0 ? f.variable_sl_high_pct : 70.0) : 70.0,
                    sl_pct: nextVal ? nextLowPct : f.sl_pct
                  };
                });
              }} />
            </div>
          </div>

          <p className="text-[11px] text-muted-foreground font-medium mb-6 leading-relaxed">
            DCA 봇의 가동 차수(물타기 횟수)에 따라 손절 라인을 다르게 적용합니다. 초반 차수와 후반 차수의 리스크를 분리 관리할 수 있습니다.
          </p>

          {form.use_variable_stop_loss && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-3">
                <label className="text-[11px] font-black text-foreground/70 uppercase">기준 차수 (Step)</label>
                <div className="relative">
                  <input type="number" className="w-full h-9 rounded-xl border border-border px-3 font-black bg-white text-sm focus:ring-2 focus:ring-rose-500/20 outline-none transition-all" {...field('variable_sl_threshold_step')} />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-bold text-sm">차 이하</span>
                </div>
                <p className="text-[10px] text-muted-foreground font-medium leading-snug">
                  이 차수 이하일 때는 아래의 기준이하 손절(ROE%)이 작동합니다. (기본: 4)
                </p>
              </div>

              <div className="space-y-3">
                <label className="text-[11px] font-black text-foreground/70 uppercase">기준이하 손절 (ROE%)</label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.1"
                    className="w-full h-9 rounded-xl border border-border px-3 font-black bg-white text-sm focus:ring-2 focus:ring-rose-500/20 outline-none transition-all"
                    value={form.variable_sl_low_pct ?? ''}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      setForm((f: any) => ({
                        ...f,
                        variable_sl_low_pct: val,
                        sl_pct: val
                      }));
                    }}
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-bold text-sm">%</span>
                </div>
                <p className="text-[10px] text-muted-foreground font-medium leading-snug">
                  기준 차수 이하 단계에서 활성화될 손절 비율입니다. (기본: 55.0)
                </p>
              </div>

              <div className="space-y-3">
                <label className="text-[11px] font-black text-foreground/70 uppercase">기준초과 손절 (ROE%)</label>
                <div className="relative">
                  <input type="number" step="0.1" className="w-full h-9 rounded-xl border border-border px-3 font-black bg-white text-sm focus:ring-2 focus:ring-rose-500/20 outline-none transition-all" {...field('variable_sl_high_pct')} />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-bold text-sm">%</span>
                </div>
                <p className="text-[10px] text-muted-foreground font-medium leading-snug">
                  기준 차수 초과 단계에서 활성화될 손절 비율입니다. (기본: 70.0)
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="col-span-full bg-emerald-500/5 border border-emerald-500/10 rounded-2xl p-4 space-y-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-600 text-white border-none text-[10px] font-black uppercase">Trailing TP</Badge>
              <h4 className="text-[14px] font-black text-emerald-600 uppercase">트레일링 익절 설정 (DCA)</h4>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-muted-foreground uppercase">{form.use_trailing_tp ? '활성화됨' : '비활성'}</span>
              <input type="checkbox" className="w-5 h-5 rounded-lg border-border text-emerald-600 cursor-pointer" checked={form.use_trailing_tp || false} onChange={(e) => setForm((f: any) => ({ ...f, use_trailing_tp: e.target.checked }))} id='use_trailing_tp' />
            </div>
          </div>

          <p className="text-[11px] text-muted-foreground font-medium mb-6 leading-relaxed">
            DCA 포지션의 이익이 활성화 수익률에 도달한 후, 최고점 대비 설정된 콜백 비율만큼 되돌릴 때 일괄 익절하여 이익을 극대화합니다.
          </p>

          {form.use_trailing_tp && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <label className="text-[11px] font-black text-foreground/70 uppercase">활성화 수익률 (ROE%(without leverage) - 생략 시 기본 익절률 사용)</label>
                <div className="relative">
                  <input type="number" step="0.1" className="w-full h-9 rounded-xl border border-border px-3 font-black bg-white text-sm focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all" {...field('trailing_activation_pct')} placeholder="예: 1.5" />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-bold text-sm">%</span>
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-[11px] font-black text-foreground/70 uppercase">되돌림 콜백 비율 (%)</label>
                <div className="relative">
                  <input type="number" step="0.05" className="w-full h-9 rounded-xl border border-border px-3 font-black bg-white text-sm focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all" {...field('trailing_callback_pct')} />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-bold text-sm">%</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="col-span-full bg-indigo-500/5 border border-indigo-500/10 rounded-2xl p-4 space-y-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Badge className="bg-indigo-600 text-white border-none text-[10px] font-black uppercase">Rebound</Badge>
              <h4 className="text-[14px] font-black text-indigo-600 uppercase">반등 시 물량 덜어내기 (Rebound Reduction)</h4>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-muted-foreground uppercase">{form.use_rebound_reduction ? '활성화됨' : '비활성'}</span>
              <input type="checkbox" className="w-5 h-5 rounded-lg border-border text-indigo-600" checked={form.use_rebound_reduction || false} onChange={(e) => setForm((f: any) => ({ ...f, use_rebound_reduction: e.target.checked }))} />
            </div>
          </div>

          <p className="text-[11px] text-muted-foreground font-medium mb-6 leading-relaxed">
            물타기 진행 중 최저가(숏은 최고가) 대비 일정 비율 반등 시, 포지션 일부를 축소하여 평단가를 개선하고 리스크를 최소화합니다.
          </p>

          {form.use_rebound_reduction && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-3">
                <label className="text-[11px] font-black text-foreground/70 uppercase">작동 최소 차수 (Step)</label>
                <div className="relative">
                  <input type="number" className="w-full h-9 rounded-xl border border-border px-3 font-black bg-white text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none transition-all" {...field('rebound_trigger_step')} />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-bold text-sm">차수</span>
                </div>
                <p className="text-[10px] text-muted-foreground font-medium leading-snug">
                  이 차수 이상의 물타기가 진행되었을 때만 반등 감축이 작동합니다. (기본: 2)
                </p>
              </div>

              <div className="space-y-3">
                <label className="text-[11px] font-black text-foreground/70 uppercase">반등 기준 (%)</label>
                <div className="relative">
                  <input type="number" step="0.1" className="w-full h-9 rounded-xl border border-border px-3 font-black bg-white text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none transition-all" {...field('rebound_pct')} />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-bold text-sm">%</span>
                </div>
                <p className="text-[10px] text-muted-foreground font-medium leading-snug">
                  최저점(숏은 최고점) 대비 이 비율만큼 반등했을 때 감축이 트리거됩니다. (기본: 0.5)
                </p>
              </div>

              <div className="space-y-3">
                <label className="text-[11px] font-black text-foreground/70 uppercase">감축 물량 비율 (%)</label>
                <div className="relative">
                  <input type="number" className="w-full h-9 rounded-xl border border-border px-3 font-black bg-white text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none transition-all" {...field('rebound_reduction_ratio')} />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-bold text-sm">%</span>
                </div>
                <p className="text-[10px] text-muted-foreground font-medium leading-snug">
                  기본적으로 마지막 회차 물량이 감축되며, 해당 값이 없을 때 전체 수량 대비 덜어낼 비율입니다. (기본: 50)
                </p>
              </div>
            </div>
          )}
        </div>


        <div className="col-span-full bg-danger/5 border border-danger/10 rounded-2xl p-4 space-y-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Badge className="bg-danger text-white border-none text-[10px] font-black uppercase">Safety</Badge>
              <h4 className="text-[14px] font-black text-danger uppercase">자동 청산 방지 (AUTO SLASHING)</h4>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-muted-foreground uppercase">{form.use_auto_slashing ? '활성화됨' : '비활성'}</span>
              <input type="checkbox" className="w-5 h-5 rounded-lg border-border text-rose-500" checked={form.use_auto_slashing} onChange={(e) => setForm((f: any) => ({ ...f, use_auto_slashing: e.target.checked }))} />
            </div>
          </div>

          <p className="text-[11px] text-muted-foreground font-medium mb-6 leading-relaxed">
            포지션이 청산가에 근접할 경우(건강도 100% 기준), 설정한 임계치에서 포지션 일부를 강제 정리하여 전체 시드 청산을 방지합니다.
          </p>

          {form.use_auto_slashing && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-3">
                <label className="text-[11px] font-black text-foreground/70 uppercase">실행 건강도 (100%=청산)</label>
                <div className="relative">
                  <input type="number" className="w-full h-9 rounded-xl border border-border px-3 font-black bg-white text-sm focus:ring-2 focus:ring-rose-500/20 outline-none transition-all" {...field('auto_slashing_health')} />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-bold text-sm">%</span>
                </div>
                <p className="text-[10px] text-muted-foreground font-medium leading-snug">
                  100% 도달 시 청산이며, 값이 높을수록 미리 작동합니다. (예: 10배 레버리지 기준 130% 설정 시 원금 93.5% 손실 시점 발동)
                </p>
              </div>

              <div className="space-y-3">
                <label className="text-[11px] font-black text-foreground/70 uppercase">포지션 축소 비율 (%)</label>
                <div className="relative">
                  <input type="number" className="w-full h-9 rounded-xl border border-border px-3 font-black bg-white text-sm focus:ring-2 focus:ring-rose-500/20 outline-none transition-all" {...field('auto_slashing_pct')} />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-bold text-sm">%</span>
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-[11px] font-black text-foreground/70 uppercase">유지 마진율 (MMR)</label>
                <div className="relative">
                  <input type="number" step="0.01" className="w-full h-9 rounded-xl border border-border px-3 font-black bg-white text-sm focus:ring-2 focus:ring-rose-500/20 outline-none transition-all" {...field('auto_slashing_mmr')} />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-bold text-sm">%</span>
                </div>
                <p className="text-[10px] text-muted-foreground font-medium leading-snug">
                  포지션 유지를 위한 최소 증거금 비율입니다. (보통 0.5%) 이 수치에 도달하면 건강도가 100%가 되어 청산됩니다.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
